import { useState, useEffect, useRef } from 'react';
import { X, Camera } from 'lucide-react';
import AIIcon from './AIIcon';
import AIChatWindow from './AIChatWindow';
import { aiApi } from '@/api/ai';

type Vendor = 'gemini' | 'anthropic' | 'deepseek';

export default function AISearchBar() {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [activeCommand, setActiveCommand] = useState<{ text: string; id: number } | null>(null);
  const [captureRequestId, setCaptureRequestId] = useState<number | null>(null);
  const [vendor, setVendor] = useState<Vendor>('gemini');
  const inputRef = useRef<HTMLInputElement>(null);
  const shortcutLabel = /Mac|iPhone|iPad|iPod/.test(navigator.platform) ? '⌘K' : 'Ctrl K';

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && !event.shiftKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setIsOpen(true);
      }
      if (event.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    const timer = window.setTimeout(() => inputRef.current?.focus(), 50);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Keep the configured active provider without exposing model controls in the prompt.
    void aiApi.getAdminConfig().then(config => {
      if (!active) return;
      const vendors = (['gemini', 'anthropic', 'deepseek'] as const).filter(candidate =>
        (config.models || []).some(model => model.vendor === candidate && model.is_active),
      );
      setVendor(current => vendors.includes(current) ? current : vendors[0] ?? 'gemini');
    }).catch(error => console.error(error));
    return () => {
      active = false;
      window.clearTimeout(timer);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const handleSearch = (text: string) => {
    setActiveCommand(text.trim() ? { text, id: Date.now() } : null);
    setIsChatOpen(true);
    setIsOpen(false);
    setQuery('');
  };

  const openChatWithCapture = () => {
    setActiveCommand(null);
    setCaptureRequestId(Date.now());
    setIsChatOpen(true);
    setIsOpen(false);
    setQuery('');
  };

  return (
    <>
      <button type="button" onClick={() => setIsOpen(true)} aria-label="AI'ya sor" title={`AI'ya sor (${shortcutLabel})`}
        className="group relative flex h-9 w-28 items-center gap-2 rounded-xl border border-slate-100 bg-slate-100/60 px-3 shadow-sm transition-all duration-300 hover:border-blue-200 hover:bg-white hover:shadow-md">
        <AIIcon size={14} className="shrink-0 text-slate-400 transition-colors group-hover:text-blue-500" />
        <span className="flex-1 truncate text-left text-xs font-semibold text-slate-400 group-hover:text-slate-500">AI'ya sor</span>
      </button>

      {isOpen && (
        <div className="fixed inset-0 z-[200] flex items-start justify-center pt-[15vh]">
          <div className="absolute inset-0 bg-slate-900/20 backdrop-blur-[2px] animate-in fade-in duration-500" onClick={() => setIsOpen(false)} />
          <div role="dialog" aria-modal="true" aria-label="AI'ya sor" className="relative mx-4 w-full max-w-2xl overflow-hidden rounded-[32px] border border-white/50 bg-white/90 font-sans shadow-[0_30px_90px_-20px_rgba(0,0,0,0.3)] backdrop-blur-xl animate-in zoom-in-95 duration-300">
            <div className="p-6">
              <div className="flex items-center gap-4 rounded-2xl border border-slate-200/50 bg-white/60 p-2 shadow-sm transition-all focus-within:border-blue-400/50 focus-within:ring-4 focus-within:ring-blue-500/5">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-200/50"><AIIcon size={20} /></div>
                <input ref={inputRef} type="text" aria-label="AI sorusu" value={query} onChange={event => setQuery(event.target.value)}
                  onKeyDown={event => { if (event.key === 'Enter') handleSearch(query); }} placeholder="Ne öğrenmek istersiniz?"
                  className="min-w-0 flex-1 bg-transparent py-2 text-xl font-bold text-slate-800 outline-none placeholder:text-slate-400/50" />
                {query && <button type="button" onClick={() => { setQuery(''); inputRef.current?.focus(); }} aria-label="Soruyu temizle" className="shrink-0 rounded-xl p-2 text-slate-400 transition-colors hover:bg-black/5"><X size={20} /></button>}
                <button type="button" onClick={openChatWithCapture} title="Ekrandan alan seçip AI ile yorumlat" aria-label="Ekrandan alan seçip AI ile yorumlat"
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-violet-200 bg-violet-50 text-violet-700 transition hover:bg-violet-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-400"><Camera size={18} /></button>
                <kbd title="AI kısayolu" className="mr-2 shrink-0 rounded-md border border-slate-200 bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-500">{shortcutLabel}</kbd>
              </div>
            </div>
          </div>
        </div>
      )}

      <AIChatWindow isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} command={activeCommand} initialVendor={vendor} initialGeminiVersion="3" captureRequestId={captureRequestId ?? undefined} />
    </>
  );
}
